import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { Card, Button, Spinner, Modal } from 'react-bootstrap';
import { FaTools, FaExclamationTriangle, FaSort, FaSortUp, FaSortDown, FaExclamationCircle, FaCog, FaCheckCircle, FaExclamationCircle as FaExclamationCircleAlt, FaHeartbeat, FaCalendarAlt } from 'react-icons/fa';
import ThumbnailStrip from './Components/Shared/ThumbnailStrip';
import InspectionDetailsModal from './Components/Shared/InspectionDetailsModal';
import EquipmentCarousel from './Components/Equipment/EquipmentCarousel';
import ServiceHistoryTable from './Components/Equipment/ServiceHistoryTable';
import Select from 'react-select';

import { useOutletContext } from 'react-router-dom';
import { checkMechanicalApiHealth, getAllIssues, getAllMaintenanceHistory, getMechanicalOverview, updateIssueStatus } from './api/mechanicalApi';

// Custom styles for react-select to match dashboard
const customSelectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    minHeight: '48px',
    width: '100%',
    ':hover': { border: 'none' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#999',
    ':hover': {
      color: '#666',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#999',
    ':hover': {
      color: '#666',
    },
  }),
};

function Pill({ color = '#ef4444', bg = '#fee2e2', children }) {
  return (
    <span style={{ background: bg, color, borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
      {children}
    </span>
  );
}

function CircularProgress({ percentage, size = 80, animated = true }) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const [animatedColor, setAnimatedColor] = useState('#dc2626');
  
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (animatedPercentage / 100) * circumference;
  
  // Color logic based on percentage (same as Hudson AI Score)
  const getColor = (score) => {
    if (score <= 20) return '#dc2626'; // red
    if (score <= 40) return '#ea580c'; // orange
    if (score <= 60) return '#d97706'; // yellow-orange
    if (score <= 80) return '#65a34a'; // yellow-green
    return '#16a34a'; // green
  };
  
  const finalColor = getColor(percentage);
  
  useEffect(() => {
    if (!animated) {
      setAnimatedPercentage(percentage);
      setAnimatedColor(finalColor);
      return;
    }
    
    // Animate the percentage
    const duration = 1500; // 1.5 seconds
    const steps = 60; // 60 steps for smooth animation
    const stepDuration = duration / steps;
    const stepSize = percentage / steps;
    
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const newPercentage = Math.min(stepSize * currentStep, percentage);
      setAnimatedPercentage(newPercentage);
      
      // Update color as it animates
      setAnimatedColor(getColor(newPercentage));
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedPercentage(percentage);
        setAnimatedColor(finalColor);
      }
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [percentage, animated, finalColor]);
  
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="6"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={animatedColor}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: animated ? 'stroke 0.1s ease-out' : 'none'
          }}
        />
      </svg>
      {/* Percentage text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#374151'
      }}>
        {Math.round(animatedPercentage)}%
      </div>
    </div>
  );
}

function CalendarOverview({ upcomingDates = [], pastDates = [] }) {
  const today = new Date();
  const dates = [];
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Generate 15 days: 7 past, today, 7 future
  for (let i = -7; i <= 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  
  const formatDate = (date) => {
    return date.getDate().toString();
  };
  
  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const getDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };
  
  const getDateStatus = (date) => {
    const dateKey = formatDateKey(date);
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) return 'today';
    if (upcomingDates.includes(dateKey)) return 'scheduled';
    if (pastDates.includes(dateKey)) return 'completed';
    return 'normal';
  };
  
  const getTooltipText = (date, status) => {
    const dateKey = formatDateKey(date);
    const dateString = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    switch (status) {
      case 'today':
        // Check if today has a scheduled visit
        if (upcomingDates.includes(dateKey)) {
          return `Today - Visit scheduled for ${dateString}`;
        }
        return `Today - ${dateString}`;
      case 'scheduled':
        return `Upcoming visit scheduled for ${dateString}`;
      case 'completed':
        return `Maintenance visit completed on ${dateString}`;
      default:
        return `No scheduled visits on ${dateString}`;
    }
  };
  
  const getDateStyle = (status) => {
    const baseStyle = {
      width: '50px',
      height: '60px',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      fontWeight: '600',
      margin: '0 2px',
      transition: 'all 0.2s ease',
      textAlign: 'center',
      position: 'relative'
    };
    
    switch (status) {
      case 'today':
        return {
          ...baseStyle,
          backgroundColor: '#e0e7ff',
          color: '#3730a3',
          border: '2px solid #6366f1'
        };
      case 'scheduled':
        return {
          ...baseStyle,
          backgroundColor: '#dcfce7',
          color: '#166534',
          border: '2px solid #16a34a'
        };
      case 'completed':
        return {
          ...baseStyle,
          backgroundColor: '#f3f4f6',
          color: '#6b7280',
          border: '2px solid #d1d5db'
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: '#f9fafb',
          color: '#374151',
          border: '1px solid #e5e7eb'
        };
    }
  };
  
  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '2rem 1rem',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        overflowX: 'auto',
        minHeight: '140px'
      }}>
        {dates.map((date, index) => {
          const status = getDateStatus(date);
          const isToday = date.toDateString() === today.toDateString();
          return (
            <div
              key={index}
              style={getDateStyle(status)}
              onMouseEnter={(e) => {
                setHoveredDate({ date, status });
                const rect = e.target.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top - 30
                });
              }}
              onMouseLeave={() => setHoveredDate(null)}
            >
              <div style={{ fontSize: '0.6rem', marginBottom: '2px', opacity: 0.8 }}>
                {getDayName(date)}
              </div>
              <div style={{ fontSize: '0.875rem', fontWeight: '700' }}>
                {formatDate(date)}
              </div>
              {isToday && (
                <div style={{
                  position: 'absolute',
                  bottom: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '0.6rem',
                  color: '#6366f1',
                  fontWeight: '600'
                }}>
                  ↓ Today
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1.5rem',
        fontSize: '0.75rem',
        color: '#6b7280',
        marginTop: '2rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#6366f1' }}></div>
          <span>Today</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#16a34a' }}></div>
          <span>Scheduled Maintenance Visit</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#d1d5db' }}></div>
          <span>Past Maintenance Visit</span>
        </div>
      </div>
      
      {/* Custom Tooltip */}
      {hoveredDate && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: '500',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            whiteSpace: 'nowrap'
          }}
        >
          {getTooltipText(hoveredDate.date, hoveredDate.status)}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #1f2937'
            }}
          />
        </div>
      )}
    </div>
  );
}

function SummaryCards({ summary, onButtonClick, totalMaintenanceVisits }) {
  if (!summary) return null;

  // Calculate color based on health score (0-100) - same logic as Hudson AI Score
  const getHealthColor = (score) => {
    if (score <= 20) return '#dc2626'; // red
    if (score <= 40) return '#ea580c'; // orange
    if (score <= 60) return '#d97706'; // yellow-orange
    if (score <= 80) return '#65a30d'; // yellow-green
    return '#16a34a'; // green
  };

  const cards = [
    {
      title: 'Equipment Covered',
      value: summary.totalEquipment,
      color: '#3b82f6',
      icon: FaCog,
      buttonText: 'Maintenance Schedule →',
      buttonType: 'schedule'
    },
    {
      title: 'Active Issues',
      value: summary.activeIssues,
      color: '#ea580c',
      icon: FaExclamationCircleAlt,
      buttonText: 'Resolve Issues →',
      buttonType: 'issues'
    },
    {
      title: 'Total Maintenance Visits',
      value: totalMaintenanceVisits,
      color: '#10b981',
      icon: FaCheckCircle,
      buttonText: 'See Visit History →',
      buttonType: 'history'
    },
    {
      title: 'Overall Health Score',
      value: `${summary.overallHealthPercent}%`,
      color: '#dc2626',
      icon: FaHeartbeat,
      buttonText: 'View AI Suggestions →',
      buttonType: 'suggestions'
    }
  ];

  return (
    <div className="mechanical-summary-cards" style={{
      display: 'flex', 
      gap: '1rem', 
      marginBottom: '2rem',
      padding: '0 1.5rem',
      flexWrap: 'wrap'
    }}>
      {cards.map((card, index) => {
        const IconComponent = card.icon;
        return (
          <div
            key={index}
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              flex: '1',
              minWidth: '200px',
              textAlign: 'center',
              minHeight: '180px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#374151',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              {card.title === 'Overall Health Score' ? (
                <CircularProgress 
                  key={`health-${summary.overallHealthPercent}`}
                  percentage={summary.overallHealthPercent} 
                  size={70} 
                  animated={true} 
                />
              ) : (
              <div style={{
                  width: '70px',
                  height: '70px',
                borderRadius: '50%',
                  border: `5px solid ${card.color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                padding: '4px'
              }}>
                  <span style={{ fontSize: '1.75rem', fontWeight: '700', color: '#374151' }}>
                  {card.value}
                </span>
              </div>
              )}
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.8rem',
              color: '#6b7280',
              fontWeight: '500',
              marginBottom: '1rem'
            }}>
              <IconComponent style={{ fontSize: '0.875rem', color: card.color }} />
              {card.title}
            </div>
            <button
              onClick={() => onButtonClick && onButtonClick(card.buttonType)}
              style={{
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: '700',
                cursor: 'pointer',
                width: '180px',
                transition: 'background-color 0.2s ease',
                display: 'block',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
              }}
            >
              {card.buttonText}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Helper function to extract date string from date object or string
function getDateString(dateValue) {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') return dateValue;
  if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
  return '';
}

// Helper function to format date for display
function formatDate(dateValue) {
  const dateStr = getDateString(dateValue);
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
}

function UnifiedIssuesTable({ issues, equipment, onOpenInspectionDetails, navigate, onStatusToggle }) {
  const [sortColumn, setSortColumn] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [equipmentFilter, setEquipmentFilter] = useState(() => {
    const saved = sessionStorage.getItem('issuesEquipmentFilter');
    return saved || 'all';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    const saved = sessionStorage.getItem('issuesStatusFilter');
    return saved || 'Ongoing';
  });

  const normalizedIssues = Array.isArray(issues) ? issues : [];
  const normalizedEquipment = Array.isArray(equipment) ? equipment : [];

  // Create a map for equipment lookup
  const equipmentMap = useMemo(() => {
    const map = {};
    normalizedEquipment.forEach(eq => {
      map[eq.id] = eq;
    });
    return map;
  }, [normalizedEquipment]);

  // Combine issues with equipment data
  const enrichedIssues = useMemo(() => {
    return normalizedIssues.map(issue => ({
      ...issue,
      equipmentName: equipmentMap[issue.equipmentId]?.name || 'Unknown Equipment',
      equipmentType: equipmentMap[issue.equipmentId]?.type || 'unknown'
    }));
  }, [normalizedIssues, equipmentMap]);

  const filteredIssues = useMemo(() => {
    return enrichedIssues.filter(issue => {
      const equipmentMatch = equipmentFilter === 'all' || issue.equipmentId === equipmentFilter;
      const statusMatch = statusFilter === 'all' || issue.status === statusFilter;
      return equipmentMatch && statusMatch;
    });
  }, [enrichedIssues, equipmentFilter, statusFilter]);

  const sorted = useMemo(() => {
    const data = [...filteredIssues];
    data.sort((a, b) => {
      let va = a[sortColumn];
      let vb = b[sortColumn];
      
      if (sortColumn === 'dateFound') {
        const dateStrA = getDateString(va);
        const dateStrB = getDateString(vb);
        if (!dateStrA && !dateStrB) return 0;
        if (!dateStrA) return 1; // Put items without dates at the end
        if (!dateStrB) return -1;
        va = new Date(dateStrA).getTime();
        vb = new Date(dateStrB).getTime();
      } else if (sortColumn === 'priority') {
        const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
        va = priorityOrder[va] || 999;
        vb = priorityOrder[vb] || 999;
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredIssues, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sorted.length / rowsPerPage) || 1;
  const start = (currentPage - 1) * rowsPerPage;
  const pageData = sorted.slice(start, start + rowsPerPage);

  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  function getStatusPill(status) {
    const baseStyle = {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textAlign: 'center',
      display: 'inline-block',
      minWidth: '70px'
    };

    switch (status) {
      case 'Ongoing':
        return {
          ...baseStyle,
          backgroundColor: '#fef2f2',
          color: '#dc2626'
        };
      case 'Resolved':
        return {
          ...baseStyle,
          backgroundColor: '#f0fdf4',
          color: '#16a34a'
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: '#f3f4f6',
          color: '#6b7280'
        };
    }
  }

  function getSortIcon(column) {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: 4, fontSize: '0.75rem', color: '#9ca3af' }} />;
    return sortDirection === 'asc' 
      ? <FaSortUp style={{ marginLeft: 4, fontSize: '0.75rem', color: '#374151' }} />
      : <FaSortDown style={{ marginLeft: 4, fontSize: '0.75rem', color: '#374151' }} />;
  }


  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
      {/* Filter Controls */}
      <div className="mechanical-issue-filters" style={{
        padding: '1rem', 
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Select
            className="mechanical-filter-select"
            aria-label="Filter issues by equipment"
            value={{ value: equipmentFilter, label: equipmentFilter === 'all' ? 'All Equipment' : normalizedEquipment.find(eq => eq.id === equipmentFilter)?.name || '' }}
            onChange={(selectedOption) => {
              setEquipmentFilter(selectedOption.value);
              setCurrentPage(1);
              sessionStorage.setItem('issuesEquipmentFilter', selectedOption.value);
            }}
            options={[
              { value: 'all', label: 'All Equipment' },
              ...normalizedEquipment.map(eq => ({ value: eq.id, label: eq.name }))
            ]}
            styles={customSelectStyles}
            isClearable={false}
            components={{
              IndicatorSeparator: () => null
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Select
            className="mechanical-filter-select"
            aria-label="Filter issues by status"
            value={{ 
              value: statusFilter, 
              label: statusFilter === 'all' ? 'All Status' : statusFilter 
            }}
            onChange={(selectedOption) => {
              setStatusFilter(selectedOption.value);
              setCurrentPage(1);
              sessionStorage.setItem('issuesStatusFilter', selectedOption.value);
            }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'Ongoing', label: 'Ongoing' },
              { value: 'Resolved', label: 'Resolved' }
            ]}
            styles={customSelectStyles}
            isClearable={false}
            components={{
              IndicatorSeparator: () => null
            }}
          />
        </div>
      </div>
      <div className="manager-table-scroll" role="region" aria-label="Equipment issues, scroll horizontally for all columns" tabIndex={0} style={{ overflowX: 'auto', padding: '1rem' }}>
        <table className="table mechanical-issues-table" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'equipmentName' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('equipmentName'); } }} onClick={() => handleSort('equipmentName')}>
                Equipment{getSortIcon('equipmentName')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'title' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('title'); } }} onClick={() => handleSort('title')}>
                Issue{getSortIcon('title')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'dateFound' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('dateFound'); } }} onClick={() => handleSort('dateFound')}>
                Date Found{getSortIcon('dateFound')}
              </th>
              <th style={{ fontWeight: '600', color: '#374151' }}>Attachments</th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'priority' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('priority'); } }} onClick={() => handleSort('priority')}>
                Priority{getSortIcon('priority')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'status' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('status'); } }} onClick={() => handleSort('status')}>
                Status{getSortIcon('status')}
              </th>
              <th style={{ fontWeight: '600', color: '#374151' }}>Report</th>
            </tr>
          </thead>
          <tbody>
            {filteredIssues.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  No issues found matching the current filters
                </td>
              </tr>
            ) : (
              pageData.map((row) => (
              <tr
                key={row.id}
                style={{
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  const cells = e.currentTarget.querySelectorAll('td');
                  cells.forEach(cell => {
                    cell.style.backgroundColor = '#f3f4f6';
                  });
                }}
                onMouseLeave={(e) => {
                  const cells = e.currentTarget.querySelectorAll('td');
                  cells.forEach(cell => {
                    cell.style.backgroundColor = '';
                  });
                }}
                onClick={() => {
                  if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(row, 'issue');
                }}
              >
                <td style={{ color: '#374151', fontWeight: '500' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (navigate && row.equipmentId) {
                        navigate(`/mechanical-room/equipment/${row.equipmentId}`);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#1D40AF',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit',
                      fontWeight: '700'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#1D40AF';
                    }}
                  >
                    {row.equipmentName}
                  </button>
                </td>
                <td style={{ color: '#374151' }}>{row.title}</td>
                <td style={{ color: '#374151' }}>{formatDate(row.dateFound)}</td>
                <td>
                  {row.photos?.length ? (
                    <ThumbnailStrip images={row.photos} label="issue photo" onOpen={(index) => onOpenInspectionDetails?.({ ...row, initialImageIndex: index }, 'issue')} />
                  ) : null}
                </td>
                <td>
                  {row.priority === 'High' ? (
                    <Pill color="#b91c1c" bg="#fee2e2">High</Pill>
                  ) : row.priority === 'Medium' ? (
                    <Pill color="#ea580c" bg="#fed7aa">Medium</Pill>
                  ) : (
                    <Pill color="#ca8a04" bg="#fef3c7">Low</Pill>
                  )}
                </td>
                <td style={{ color: '#374151' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onStatusToggle === 'function') {
                        onStatusToggle(row);
                      }
                    }}
                    style={{
                      ...getStatusPill(row.status),
                      border: getStatusPill(row.status).border || '1px solid transparent',
                      backgroundColor: getStatusPill(row.status).backgroundColor,
                      cursor: 'pointer'
                    }}
                  >
                    {row.status}
                  </button>
                </td>
                <td>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(row, 'issue');
                    }}
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                    }}
                  >
                    <FaExclamationCircle style={{ fontSize: '0.75rem', color: '#9ca3af' }} />
                            View Issue Report
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="manager-table-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 16px' }}>
        <div>
          <label style={{ marginRight: 8, color: '#6b7280' }}>Rows per page</label>
          <select
            aria-label="Issues per page"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="form-select"
            style={{ width: 100, display: 'inline-block' }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
        <div style={{ color: '#6b7280' }}>
          <button className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 8px' }}>{currentPage} / {totalPages}</span>
          <button className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      </div>
    </div>
  );
}

function MechanicalRoom() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext?.() || {};
  const {
    buildingName,
    buildingPhotoUrl,
    maintenanceCycles,
    selectedCycle,
    setSelectedCycle,
    selectedMaintenance,
    hasMechanicalContract,
    isLoading,
    buildingId,
  } = context;

  // Layout handles data fetch; keep component focused on content

  const handleNavigate = () => {};

  function openInspectionDetails(data, type) {
    // Find equipment name for issues
    let equipmentName = '';
    if (type === 'issue' && data.equipmentId) {
      const equipment = overviewData.equipment.find(eq => eq.id === data.equipmentId);
      equipmentName = equipment ? equipment.name : 'Unknown Equipment';
    } else if (type === 'service') {
      // For service history, equipment name is already in the data (can be array or string)
      if (Array.isArray(data.equipmentName)) {
        equipmentName = data.equipmentName.join(', ');
      } else {
        equipmentName = data.equipmentName || '';
      }
    }
    
    setInspectionData({ 
      ...data, 
      type,
      details: data.description || data.details, // Map description or details for the modal
      equipmentName // Add equipment name for modal title
    });
    setShowInspectionModal(true);
    
    // Update URL with query parameter for shareable link
    const params = new URLSearchParams(searchParams);
    if (type === 'issue') {
      params.set('issue', data.id);
      params.delete('service'); // Remove service param if it exists
    } else if (type === 'service') {
      params.set('service', data.id);
      params.delete('issue'); // Remove issue param if it exists
    }
    setSearchParams(params, { replace: true });
  }
  
  function closeInspectionModal() {
    // Guard to avoid re-triggering the open effect while we clear params
    closingModalRef.current = true;

    setShowInspectionModal(false);
    setInspectionData(null);

    const params = new URLSearchParams(searchParams);
    params.delete('issue');
    params.delete('service');
    setSearchParams(params, { replace: true });

    // Release guard after current tick
    setTimeout(() => {
      closingModalRef.current = false;
    }, 0);
  }

  function handleButtonClick(message) {
    setStatusMessage(message);
    setShowStatusBanner(true);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setShowStatusBanner(false), 5000);
  }

  function handleSummaryButtonClick(buttonType) {
    switch (buttonType) {
      case 'schedule':
        if (overviewData?.summary?.scheduleUrl) {
          window.open(overviewData.summary.scheduleUrl, '_blank', 'noopener,noreferrer');
        } else {
          setShowScheduleModal(true);
        }
        break;
      case 'issues':
        // Scroll to issues section
        const issuesSection = document.querySelector('[data-section="issues"]');
        if (issuesSection) {
          issuesSection.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      case 'history':
        // Scroll to maintenance history section
        const historySection = document.querySelector('[data-section="history"]');
        if (historySection) {
          historySection.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      case 'suggestions':
        setShowSuggestionsModal(true);
        break;
      default:
        break;
    }
  }

  const [overviewData, setOverviewData] = useState(null);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionData, setInspectionData] = useState(null);
  const [showStatusBanner, setShowStatusBanner] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const closingModalRef = useRef(false);
  
  async function handleIssueStatusChange(issueId, newStatus) {
    if (!issueId) return;

    // Optimistic: update modal and list
    setInspectionData((prev) => {
      if (!prev || prev.id !== issueId) return prev;
      return { ...prev, status: newStatus };
    });

    setAllIssues((prev) => {
      if (!Array.isArray(prev)) return prev;
      let previousStatus = null;
      const updated = prev.map((issue) => {
        if (issue.id === issueId) {
          previousStatus = issue.status;
          return { ...issue, status: newStatus };
        }
        return issue;
      });

      // Adjust summary active issues count if we know the previous status
      if (previousStatus && previousStatus !== newStatus) {
        setOverviewData((prevOverview) => {
          if (!prevOverview?.summary) return prevOverview;
          const delta =
            previousStatus === 'Ongoing' && newStatus === 'Resolved'
              ? -1
              : previousStatus === 'Resolved' && newStatus === 'Ongoing'
              ? 1
              : 0;
          if (delta === 0) return prevOverview;
          return {
            ...prevOverview,
            summary: {
              ...prevOverview.summary,
              activeIssues: Math.max(0, (prevOverview.summary.activeIssues || 0) + delta),
            },
          };
        });
      }

      return updated;
    });

    // Refresh issues from server to avoid cache/stale data and update session cache
    try {
      if (!buildingId) return;
      const freshIssues = await getAllIssues(buildingId);
      setAllIssues(freshIssues);

      // Update cached session data for this building to keep future loads consistent
      const cacheKey = `mechanicalRoom_${buildingId}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          parsed.issues = freshIssues;
          sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
        } catch (e) {
          console.warn('Failed to update mechanicalRoom cache after status change:', e);
        }
      }
    } catch (err) {
      console.error('Failed to refresh issues after status change', err);
    }
  }

  async function handleIssueRowToggle(issue) {
    if (!issue?.id) return;
    const nextStatus = issue.status === 'Resolved' ? 'Ongoing' : 'Resolved';
    try {
      await updateIssueStatus(issue.id, nextStatus);
      await handleIssueStatusChange(issue.id, nextStatus);
    } catch (err) {
      console.error('Failed to toggle issue status from row', err);
      alert('Failed to update status. Please try again.');
    }
  }

  // Compute unique maintenance visit dates (YYYY-MM-DD) for summary card
  const totalMaintenanceVisits = useMemo(() => {
    if (!Array.isArray(maintenanceHistory) || maintenanceHistory.length === 0) return 0;
    
    const dateSet = new Set();
    maintenanceHistory.forEach((visit) => {
      if (!visit || !visit.date) return;
      
      let dateStr = visit.date;
      if (typeof dateStr === 'object' && dateStr.iso) {
        dateStr = dateStr.iso;
      }
      if (typeof dateStr !== 'string') return;
      
      // Normalize to date-only (YYYY-MM-DD)
      const onlyDate = dateStr.split('T')[0];
      if (onlyDate) {
        dateSet.add(onlyDate);
      }
    });
    
    return dateSet.size;
  }, [maintenanceHistory]);

  // Equipment sorting state - persist in sessionStorage
  const [equipmentSortBy, setEquipmentSortBy] = useState(() => {
    const saved = sessionStorage.getItem('equipmentSortBy');
    return saved || 'mostRecentVisit';
  });

  // Equipment search state
  const [equipmentSearch, setEquipmentSearch] = useState('');

  // Sort equipment based on selected criteria
  const sortedEquipment = useMemo(() => {
    if (!overviewData?.equipment) return [];
    
    const equipment = [...overviewData.equipment];
    const toDateValue = (value) => {
      if (!value) return null;
      let dateStr = value;
      if (typeof value === 'object' && value.iso) {
        dateStr = value.iso;
      }
      const ts = Date.parse(dateStr);
      return Number.isNaN(ts) ? null : ts;
    };
    
    switch (equipmentSortBy) {
      case 'mostRecentVisit':
        return equipment.sort((a, b) => {
          const aTs = toDateValue(a.lastServiceDate);
          const bTs = toDateValue(b.lastServiceDate);
          if (aTs === null && bTs === null) return 0;
          if (aTs === null) return 1; // nulls to the bottom
          if (bTs === null) return -1;
          return bTs - aTs; // Most recent first
        });
      case 'oldestVisit':
        return equipment.sort((a, b) => {
          const aTs = toDateValue(a.lastServiceDate);
          const bTs = toDateValue(b.lastServiceDate);
          if (aTs === null && bTs === null) return 0;
          if (aTs === null) return 1; // nulls to the bottom
          if (bTs === null) return -1;
          return aTs - bTs; // Oldest first
        });
      case 'mostIssues':
        return equipment.sort((a, b) => b.issuesCount - a.issuesCount);
      case 'fewestIssues':
        return equipment.sort((a, b) => a.issuesCount - b.issuesCount);
      case 'name':
      default:
        return equipment.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [overviewData?.equipment, equipmentSortBy]);

  // Filter equipment based on search term
  const filteredEquipment = useMemo(() => {
    if (!equipmentSearch.trim()) return sortedEquipment;
    const searchLower = equipmentSearch.toLowerCase().trim();
    return sortedEquipment.filter(eq => 
      eq.name?.toLowerCase().includes(searchLower)
    );
  }, [sortedEquipment, equipmentSearch]);

  useEffect(() => {
    if (!buildingId) return;
    
    // Always run health check on every page refresh/load
    checkMechanicalApiHealth(buildingId)
      .then((healthResponse) => {
        console.log('Mechanical API health check:', healthResponse);
      })
      .catch((e) => {
        console.error('Mechanical API health check failed:', e);
      });
    
    // Check if we have cached data for this building
    const cacheKey = `mechanicalRoom_${buildingId}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const { overview, maintenance, issues, timestamp } = JSON.parse(cachedData);
        // Use cached data if it's less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          setOverviewData(overview);
          setMaintenanceHistory(maintenance);
          setAllIssues(issues);
          setIsDataLoading(false);
          return; // Exit early, but health check already ran above
        }
      } catch (e) {
        console.warn('Failed to parse cached data:', e);
      }
    }
    
    setIsDataLoading(true);
    
    // Load overview, maintenance, and issues data simultaneously
    Promise.all([
      getMechanicalOverview(buildingId),
      getAllMaintenanceHistory(buildingId),
      getAllIssues(buildingId)
    ])
      .then(([overview, maintenance, issues]) => {
        setOverviewData(overview);
        setMaintenanceHistory(maintenance);
        setAllIssues(issues);
        setIsDataLoading(false);
        
        // Cache the data
        const dataToCache = {
          overview,
          maintenance,
          issues,
          timestamp: Date.now()
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      })
      .catch((e) => {
        console.error('Failed to load mechanical room data:', e);
        setIsDataLoading(false);
      });
  }, [buildingId]);

  // Check URL parameters and open modal if needed
  useEffect(() => {
    // Only check URL params after data is loaded
    if (isDataLoading || !overviewData) return;
    
    // Don't process if modal is already open (prevents re-opening when closing)
    if (showInspectionModal) return;
    
    // Skip processing if we're in the middle of closing/cleaning params
    if (closingModalRef.current) return;

    const issueId = searchParams.get('issue');
    const serviceId = searchParams.get('service');
    
    // If no params exist, nothing to do
    if (!issueId && !serviceId) {
      return;
    }
    
    if (issueId && allIssues.length > 0) {
      // Find the issue by ID
      const issue = allIssues.find(i => i.id === issueId);
      if (issue) {
        // Find equipment name for issues
        let equipmentName = '';
        if (issue.equipmentId) {
          const equipment = overviewData.equipment.find(eq => eq.id === issue.equipmentId);
          equipmentName = equipment ? equipment.name : 'Unknown Equipment';
        }
        
        setInspectionData({ 
          ...issue, 
          type: 'issue',
          details: issue.description || issue.details,
          equipmentName
        });
        setShowInspectionModal(true);
      } else if (issueId && !issue) {
        // Issue ID in URL but not found - clean up URL
        const params = new URLSearchParams(searchParams);
        params.delete('issue');
        setSearchParams(params, { replace: true });
      }
    } else if (serviceId && maintenanceHistory.length > 0) {
      // Find the service/maintenance record by ID
      const service = maintenanceHistory.find(s => s.id === serviceId);
      if (service) {
        // For service history, equipment name is already in the data (can be array or string)
        let equipmentName = '';
        if (Array.isArray(service.equipmentName)) {
          equipmentName = service.equipmentName.join(', ');
        } else {
          equipmentName = service.equipmentName || '';
        }
        
        setInspectionData({ 
          ...service, 
          type: 'service',
          details: service.description || service.details,
          equipmentName
        });
        setShowInspectionModal(true);
      } else if (serviceId && !service) {
        // Service ID in URL but not found - clean up URL
        const params = new URLSearchParams(searchParams);
        params.delete('service');
        setSearchParams(params, { replace: true });
      }
    }
  }, [isDataLoading, overviewData, allIssues, maintenanceHistory, searchParams, showInspectionModal]);

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaTools />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            Mechanical Room Maintenance
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Streamline your mechanical room operations with Hudson's comprehensive management solution. 
            Get real-time monitoring, preventive maintenance scheduling, and detailed equipment tracking all in one place.
          </Card.Text>
          <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
            Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  if (isDataLoading) {
    return <LoadingState />;
  }

  if (!overviewData) {
    return <EmptyState />;
  }

  return (
    <div className="mechanical-overview" style={{ position: 'relative', minHeight: '100vh' }}>
      <div className="mechanical-overview-inner" style={{ width: '96.5%', marginLeft: 'auto', marginRight: 'auto' }}>
        {/* Summary Cards */}
        {overviewData?.summary && (
        <>
          <div style={{ 
            fontWeight: 600, 
            marginBottom: '1rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            padding: '0 1.5rem',
            fontSize: '1.125rem',
            color: '#1f2937'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#3b82f6'
            }}>
              <FaCog />
            </div>
            Mechanical Overview
          </div>
          <SummaryCards
            summary={overviewData.summary}
            onButtonClick={handleSummaryButtonClick}
            totalMaintenanceVisits={totalMaintenanceVisits}
          />
        </>
      )}

      {/* Equipment Overview */}
      {overviewData?.equipment && (
        <>
          <div style={{ 
            fontWeight: 600, 
            marginBottom: '0.5rem', 
            marginTop: '3rem',
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            padding: '0 1.5rem',
            fontSize: '1.125rem',
            color: '#1f2937'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#3b82f6'
            }}>
              <FaCog />
            </div>
            Equipment Overview
          </div>
          <div className="mechanical-equipment-toolbar" style={{
            fontSize: '0.875rem', 
            color: '#6b7280', 
            marginBottom: '1rem', 
            padding: '0 1.5rem',
            marginTop: '-0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '2rem'
          }}>
            <span>Click any equipment to see its service history and outstanding issues</span>
            <div className="mechanical-equipment-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: 'auto', marginRight: '10px' }}>
              {/* Equipment Search */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search equipment..."
                  aria-label="Search equipment"
                  value={equipmentSearch}
                  onChange={(e) => setEquipmentSearch(e.target.value)}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '0.75rem 1rem',
                    paddingLeft: '2.5rem',
                    fontSize: '1rem',
                    width: '280px',
                    outline: 'none'
                  }}
                />
                <svg 
                  style={{ 
                    position: 'absolute', 
                    left: '0.75rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    width: '16px',
                    height: '16px'
                  }}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {/* Equipment Sort */}
              <div style={{ position: 'relative', zIndex: 1000 }}>
              <Select
                className="mechanical-filter-select"
                aria-label="Sort equipment"
                value={{ 
                  value: equipmentSortBy, 
                  label: equipmentSortBy === 'name' ? 'Name' :
                         equipmentSortBy === 'mostRecentVisit' ? 'Most Recent Visit' :
                         equipmentSortBy === 'oldestVisit' ? 'Oldest Visit' :
                         equipmentSortBy === 'mostIssues' ? 'Most Issues' :
                         equipmentSortBy === 'fewestIssues' ? 'Fewest Issues' : 'Most Recent Visit'
                }}
                onChange={(selectedOption) => {
                  setEquipmentSortBy(selectedOption.value);
                  sessionStorage.setItem('equipmentSortBy', selectedOption.value);
                }}
                options={[
                  { value: 'mostRecentVisit', label: 'Most Recent Visit' },
                  { value: 'oldestVisit', label: 'Oldest Visit' },
                  { value: 'mostIssues', label: 'Most Issues' },
                  { value: 'fewestIssues', label: 'Fewest Issues' },
                  { value: 'name', label: 'Name' }
                ]}
                styles={{
                  ...customSelectStyles,
                  control: (base) => ({
                    ...base,
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    minHeight: '48px',
                    width: '100%',
                    ':hover': { border: 'none' },
                    fontWeight: 'normal',
                    fontSize: '1rem'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9ca3af',
                    fontWeight: 'normal',
                    fontSize: '1rem'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: '1rem',
                    color: '#000000'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 1001
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '1rem',
                    color: state.isSelected ? '#ffffff' : '#000000',
                    backgroundColor: state.isSelected ? '#0066cc' : '#ffffff',
                    ':hover': {
                      backgroundColor: state.isSelected ? '#0066cc' : '#f5f5f5',
                      color: state.isSelected ? '#ffffff' : '#000000'
                    }
                  })
                }}
                placeholder="Sort equipment by..."
                isClearable={false}
                components={{
                  IndicatorSeparator: () => null
                }}
              />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: '2rem' }}>
            {filteredEquipment.length === 0 && equipmentSearch.trim() ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1.5rem',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                margin: '0 1.5rem'
              }}>
                <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                  No equipment found matching "<strong>{equipmentSearch}</strong>"
                </div>
                <div style={{ fontSize: '0.875rem' }}>
                  Try a different search term
                </div>
              </div>
            ) : (
              <EquipmentCarousel 
                equipment={filteredEquipment} 
                onNavigate={(path) => navigate(path)} 
              />
            )}
        </div>
        </>
      )}

      {/* Active Issues */}
      {overviewData?.issues && (
        <>
          <div data-section="issues" style={{ 
            fontWeight: 600, 
            marginBottom: '1rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            padding: '0 1.5rem',
            fontSize: '1.125rem',
            color: '#1f2937'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#3b82f6'
            }}>
              <FaExclamationTriangle />
            </div>
            Issues & Recommendations
          </div>
          <div style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280', 
            marginBottom: '2rem', 
            padding: '0 1.5rem',
            marginTop: '0.5rem'
          }}>
            Active issues requiring attention and recommended actions for optimal equipment performance
          </div>
          <div style={{ marginBottom: '2rem', padding: '0 1.5rem' }}>
            <UnifiedIssuesTable
              issues={allIssues}
              equipment={overviewData.equipment}
              onOpenInspectionDetails={openInspectionDetails}
              navigate={navigate}
              onStatusToggle={handleIssueRowToggle}
            />
          </div>
        </>
      )}

      {/* Scheduled Maintenance History - Commented out for now */}
      {maintenanceHistory.length > 0 && (
        <>
          <div data-section="history" style={{ 
            fontWeight: 600, 
            marginBottom: '1rem', 
            marginTop: '3rem',
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            padding: '0 1.5rem',
            fontSize: '1.125rem',
            color: '#1f2937'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#3b82f6'
            }}>
              <FaCog />
            </div>
            Scheduled Maintenance History
          </div>
          <div style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280', 
            marginBottom: '2rem', 
            padding: '0 1.5rem',
            marginTop: '0.5rem'
          }}>
            Complete history of all scheduled maintenance visits performed across all equipment in the building
          </div>
          <div style={{ marginBottom: '2rem', padding: '0 1.5rem' }}>
            <ServiceHistoryTable 
              rows={maintenanceHistory} 
              onOpenInspectionDetails={openInspectionDetails}
              showTitle={false}
              navigate={navigate}
            />
          </div>
        </>
      )}

      {/* Technician Section */}
      <div style={{ 
        fontWeight: 600, 
        marginBottom: '1rem', 
        marginTop: '3rem',
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        padding: '0 1.5rem',
        fontSize: '1.125rem',
        color: '#1f2937'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#dbeafe',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: '#3b82f6'
        }}>
          <FaTools />
        </div>
        Dedicated Technicians
      </div>
      <div style={{ marginBottom: '2rem', padding: '0 1.5rem' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', padding: '1rem' }}>
          <div className="mechanical-technicians-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {[
              { name: 'Ergi Hoxha', specialty: 'Service Manager', phone: '416 876 2354', email: 'ergi@hudsonhvac.ca', status: 'Available' },
              { name: 'Ehsan B', specialty: 'Lead Technician', phone: '437 448 0640', email: 'ehsan@hudsonhvac.ca', status: 'Available' }
            ].map((technician, index) => (
              <div key={index} style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h6 style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>{technician.name}</h6>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: technician.status === 'Available' ? '#059669' : 
                           technician.status === 'On Call' ? '#d97706' : '#dc2626',
                    backgroundColor: technician.status === 'Available' ? '#d1fae5' : 
                                   technician.status === 'On Call' ? '#fef3c7' : '#fee2e2',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontWeight: '500'
                  }}>
                    {technician.status}
                  </span>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  {technician.specialty}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  📞 {technician.phone}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  ✉️ {technician.email}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {showStatusBanner && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            border: '1px solid #c3e6cb',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          <strong>{statusMessage}</strong>
        </div>
      )}

      {/* Inspection Details Modal */}
      <InspectionDetailsModal
        show={showInspectionModal}
        onClose={closeInspectionModal}
        data={inspectionData}
        type={inspectionData?.type}
        onButtonClick={handleButtonClick}
        onStatusChange={handleIssueStatusChange}
        buildingName={buildingName}
      />

      {/* Schedule Modal — shown only when no scheduleUrl is available */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} centered size="lg">
        <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Modal.Title>Maintenance Schedule</Modal.Title>
          <Button 
            variant="link" 
            onClick={() => setShowScheduleModal(false)} 
            style={{ 
              textDecoration: 'none', 
              color: '#000', 
              fontSize: '1.5rem',
              padding: '0',
              border: 'none',
              background: 'none'
            }}
          >
            ×
          </Button>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '1.5rem' }}>
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <CalendarOverview 
              upcomingDates={overviewData?.summary?.upcomingServiceDates || []}
              pastDates={maintenanceHistory
                .filter(visit => {
                  const visitDate = new Date(visit.date);
                  const today = new Date();
                  const sevenDaysAgo = new Date(today);
                  sevenDaysAgo.setDate(today.getDate() - 7);
                  return visitDate >= sevenDaysAgo && visitDate < today;
                })
                .map(visit => visit.date)
              }
            />
            
            <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.6', maxWidth: '700px', margin: '2.5rem auto 2rem auto' }}>
              This calendar shows your recent and upcoming maintenance visits.
            </p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Suggestions Modal */}
      <Modal show={showSuggestionsModal} onHide={() => setShowSuggestionsModal(false)} centered size="lg">
        <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Modal.Title>Smart Building Health Suggestions</Modal.Title>
          <Button 
            variant="link" 
            onClick={() => setShowSuggestionsModal(false)} 
            style={{ 
              textDecoration: 'none', 
              color: '#000', 
              fontSize: '1.5rem',
              padding: '0',
              border: 'none',
              background: 'none'
            }}
          >
            ×
          </Button>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '1.5rem' }}>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ 
              fontSize: '3rem', 
              color: '#3b82f6', 
              marginBottom: '1.5rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <FaHeartbeat />
            </div>
            <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto' }}>
              Smart AI-powered suggestions to help reduce costs, improve energy efficiency, and enhance building quality will appear here as we gather more data about your building's performance. We need additional operational data before we can provide personalized recommendations.
            </p>
          </div>
        </Modal.Body>
      </Modal>
      </div>
      </div>
  );
}

export default MechanicalRoom; 